import { injectable } from "inversify";
import io from "socket.io-client";

import { TGuid } from "next-shared/src/types/guid";
import {
  EServiceName,
  ISyncMetadata,
} from "next-shared/src/types/ISyncMetadata";
import { createGuid } from "next-shared/src/helpers/guid";
import { ISyncConnectErrorInfo } from "next-shared/src/services/BaseDataSyncService";
import { SYNC_CONNECT_ERROR, SYNC_DATA } from "../../types/sync";

/**
 * A map from name to socket URL
 */
interface ISocketUrls {
  [name: string]: string;
}

export interface ISyncModule {
  getSessionToken(serviceName?: EServiceName): string;
  setSessionToken(
    newSessionToken: string | null,
    serviceNames?: EServiceName[],
  ): void;
  setUrls(urls: { [name: string]: string }): void;
  subscribe(syncMetadata: ISyncMetadata, cb: (newData: any) => void): TGuid;
  unsubscribe(guid: TGuid): boolean;
  getSockets(): TSocketInfoMap;
}

export interface ISocketInfo {
  socket?: SocketIOClient.Socket;
  sessionToken?: string;
}

export type TSocketInfoMap = {
  [name in EServiceName]: ISocketInfo;
};

@injectable()
export class SyncModule implements ISyncModule {
  private urls: ISocketUrls;
  // named socket connections
  private readonly socketsInfo: TSocketInfoMap;

  private connections = new Map<
    TGuid,
    {
      socket: SocketIOClient.Socket;
      syncMetadata: ISyncMetadata;
      cb: (data: any) => void;
    }
  >();

  public constructor() {
    this.socketsInfo = {
      [EServiceName.Main]: {},
      [EServiceName.Actions]: {},
      [EServiceName.Hive]: {},
    };
  }

  public getSockets() {
    return this.socketsInfo;
  }

  /**
   * Creates a socket connection.
   */
  private createSocket(serviceName: EServiceName) {
    const socket = io.connect(this.urls[serviceName] + "data-sync", {
      transports: ["websocket"],
      upgrade: false,
    });

    this.socketsInfo[serviceName].socket = socket;

    // resubscribe to event scopes on socket reconnect
    socket.on("reconnect", () => {
      // HACK leaving to help with debug
      console.warn(`reconnected socket: ${serviceName}`);

      const sessionToken = this.getSessionToken(serviceName);

      if (!sessionToken) {
        console.log(
          `SyncModule - no session token, reconnectForService(${serviceName})`,
        );
        // no session active, just configure
        this.reconnectForService(serviceName);
        return;
      }

      // re-authenticate
      socket.emit("sync_authenticate", { sessionToken, serviceName }, () => {
        // authenticate before configuring
        return this.reconnectForService(serviceName);
      });
    });

    socket.on(SYNC_DATA, (guid: TGuid, newData: any) => {
      // load local callback for this guid
      const connection = this.connections.get(guid);
      if (connection === undefined) {
        return;
      }
      connection.cb(newData);
    });

    socket.on(SYNC_CONNECT_ERROR, (error: ISyncConnectErrorInfo) => {
      const { guid, message } = error;
      console.warn(`sync error, GUID: ${guid}`);

      const connection = this.connections.get(guid);
      // load local callback for this guid
      if (connection === undefined) {
        console.warn(`Received error for non-existent connection: ${guid}`);
        return;
      }
      connection.cb(new Error(message));
    });

    const sessionToken = this.getSessionToken(serviceName);

    // do we already have a session?
    if (sessionToken) {
      socket.emit("sync_authenticate", {
        sessionToken,
        serviceName,
      });
    }
  }

  /**
   * set socket connection url
   *
   * @param {string} url
   */
  public setUrls(urls: ISocketUrls) {
    this.urls = urls;
  }

  /**
   * get session token
   *
   * @param {EServiceName} serviceName optional name of the service whose token you want (defaults to next-services)
   *
   * @returns {string}
   */
  getSessionToken(
    serviceName: EServiceName = EServiceName.Main,
  ): string | null {
    return this.socketsInfo[serviceName]?.sessionToken ?? null;
  }

  /**
   * set session token
   *
   * @param {string | null} newSessionToken
   * @param {EServiceName[]} serviceNames optional list of services to update that the token can be used for (defaults to next-services and next-actions)
   */
  setSessionToken(
    newSessionToken: string | null,
    serviceNames: EServiceName[] = [EServiceName.Main, EServiceName.Actions],
  ) {
    // authenticate, this will automatically re-create all endpoints on the server
    // using the new session (allows for user switching). If the supplied token is
    // already in use on the socket, the socket is skipped.
    serviceNames
      .map((serviceName) => this.socketsInfo[serviceName])
      .filter(({ sessionToken }) => sessionToken !== newSessionToken)
      .forEach((socketInfo) => {
        socketInfo.sessionToken = newSessionToken;
        socketInfo.socket?.emit("sync_authenticate", newSessionToken);
      });
  }

  /**
   * Reconnect all sockets for a given service name.
   */
  private reconnectForService(name: string) {
    // reconnect to everything (if server dropped out we want to seamlessly re-connect to all endpoints)
    for (const connectionGuid of this.connections.keys()) {
      const { socket, syncMetadata } = this.connections.get(connectionGuid);

      const service = syncMetadata.serviceName || EServiceName.Main;
      if (name === service) {
        socket.emit("sync_connect", connectionGuid, syncMetadata);
      }
    }
  }

  /**
   * Subscribe to a subject within a socket.
   */
  public subscribe(
    syncMetadata: ISyncMetadata,
    cb: (newData: any) => void,
  ): TGuid {
    // create a guid for this connection
    const guid = createGuid();
    const socketName = syncMetadata.serviceName || EServiceName.Main;

    // create the socket on demand
    if (!this.socketsInfo[socketName].socket) {
      this.createSocket(socketName);
    }

    const { socket } = this.socketsInfo[socketName];

    // connect to websocket
    // prefix methods with "sync_" to avoid ambiguity with socket.io native events
    socket.emit("sync_connect", guid, syncMetadata);
    // store connection
    // we also store "syncMetadata" so we can re-connect if the server drops out
    this.connections.set(guid, { socket, syncMetadata, cb });

    // return guid for future disconnection
    return guid;
  }

  public unsubscribe(disconnectGuid: TGuid): boolean {
    // prefix methods with "sync_" to avoid ambiguity with socket.io native events
    // this doesn't disconnect the whole socket, just the endpoint with this guid
    let deleteCount = 0;
    for (const guid of this.connections.keys()) {
      if (disconnectGuid === guid) {
        const { socket } = this.connections.get(guid);
        socket.emit("sync_disconnect", guid);
        this.connections.delete(guid);
        deleteCount++;
        console.log(
          "sync_disconnect, remaining connections: " + this.connections.size,
        );
      }
    }
    if (deleteCount === 0) {
      console.error(
        "Unable to find connection",
        disconnectGuid,
        this.connections,
      );
    }
    if (deleteCount > 1) {
      console.warn("Found multiple connections to disconnect", disconnectGuid);
    }

    return deleteCount > 0;
  }
}
